import { RacemapColors } from '@racemap/utilities/consts/common';
import { shortIdBuilder } from '@racemap/utilities/functions/utils';
import type { Feature, FeatureCollection, GeoJsonProperties, Point, Position } from 'geojson';
import type {
  CircleLayerSpecification,
  ExpressionSpecification,
  MapMouseEvent,
  SymbolLayerSpecification,
} from 'mapbox-gl';
import { type FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Layer, type MapRef, Source } from 'react-map-gl/mapbox';
import tinycolor from 'tinycolor2';
import { MapContext, type NewDataProps } from '../BasicMap/BasicMap';
import { type AUTOFOCUS, injectFeatureIds } from '../helpers';
import {
  getStatusIconImage,
  getStatusIconImageId,
  STARTER_STATUS_ICONS,
  STATUS_ICON_COLORS,
  type StarterStatusIconKey,
} from '../helpers/getStatusIconImage';
import { type GetTextLabelImageParams, getTextLabelImage } from '../helpers/getTextLabelImage';

export interface StarterWithPosition {
  id: string;
  name: string;
  startNumber: string;
  color: string;
  position: Position;
  isMoving?: boolean;
  isAwayFromTrack?: boolean;
  alertCount?: number;
  notificationCount?: number;
}

export const getStarterPositionSourceId = (id: string): string => `starter-position-${id}`;

export interface Props {
  startersWithPosition: Array<StarterWithPosition>;
  matchingStarters?: Set<string>;
  beforeId?: string;
  onNewData?: (props: NewDataProps) => void;
  onMouseEnter?: (evt: MapMouseEvent) => void;
  onMouseLeave?: (evt: MapMouseEvent) => void;
  onClick?: (evt: MapMouseEvent) => void;
  autofocus?: boolean | AUTOFOCUS;
  id?: string;
  highlightedStarters?: Array<string | null>;
}

export const StarterPositionLayer: FC<Props> = ({
  startersWithPosition,
  highlightedStarters,
  matchingStarters,
  beforeId,
  onNewData,
  autofocus = false,
  onMouseEnter,
  onMouseLeave,
  onClick,
  id,
}) => {
  const { map } = useContext(MapContext);
  const idRef = useRef(id || shortIdBuilder());

  const features: Array<Feature<Point>> = useMemo(() => {
    const highlightedSet = new Set(highlightedStarters);

    return startersWithPosition.map((starter) => {
      const alertCount = starter.alertCount ?? 0;
      const notificationCount = starter.notificationCount ?? 0;
      const textColor = tinycolor(starter.color).isDark() ? 'white' : 'black';
      const isFiltered = matchingStarters != null && !matchingStarters.has(starter.id);

      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: starter.position,
        },
        _id: starter.id,
        properties: {
          id: starter.id,
          name: starter.name,
          startNumber: starter.startNumber,
          color: starter.color,
          textColor,
          visualization: isFiltered
            ? 'filtered-dot'
            : highlightedSet.has(starter.id)
              ? 'label'
              : 'dot',
          outlineColor:
            alertCount > 0
              ? RacemapColors.AltRed
              : notificationCount > 0
                ? RacemapColors.LightOrange
                : 'black',
          outlineWidthFactor: alertCount + notificationCount > 0 ? 2 : 1,
          alertCount: alertCount > 1 ? String(alertCount) : '',
          notificationCount: notificationCount > 1 ? String(notificationCount) : '',
          alertIcon: alertCount > 0 ? getStatusIconImageId('alert', textColor) : '',
          notificationIcon:
            notificationCount > 0 ? getStatusIconImageId('notification', textColor) : '',
          moveIcon:
            starter.isMoving == null
              ? ''
              : getStatusIconImageId(starter.isMoving ? 'moving' : 'notMoving', textColor),
          offTrackIcon:
            starter.isAwayFromTrack === true ? getStatusIconImageId('offTrack', textColor) : '',
          sortKey: alertCount > 0 ? 2 : notificationCount > 0 ? 1 : 0,
        },
      };
    });
  }, [startersWithPosition, highlightedStarters, matchingStarters]);
  const featuresWithIdProperty = useMemo(() => injectFeatureIds(features), [features]);
  const featureCollection: FeatureCollection<Point, GeoJsonProperties> = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: [...featuresWithIdProperty],
    }),
    [featuresWithIdProperty],
  );
  const colors = useMemo(
    () => new Set(startersWithPosition.map((s) => s.color)),
    [startersWithPosition],
  );
  useStarterMapLabels(map, colors);
  useStatusIconImages(map);

  const {
    setMouseEnterAction,
    setMouseLeaveAction,
    setOnClickAction,
    addInteractiveLayerIds,
    mapStyle,
  } = useContext(MapContext);
  const layers = mapStyle?.layers.map((l) => l.id) || [];

  useEffect(() => {
    addInteractiveLayerIds(`starter-position-label-layer-${idRef.current}`);
    addInteractiveLayerIds(`starter-position-dot-layer-${idRef.current}`);
  });

  useEffect(() => {
    onNewData?.({
      layer: `starter-position-label-layer-${idRef.current}`,
      data: featureCollection,
      autofocus,
    });
  }, [onNewData, idRef, featureCollection, autofocus]);

  useEffect(() => {
    if (onMouseEnter != null) {
      setMouseEnterAction(getStarterPositionSourceId(idRef.current), onMouseEnter);
    }
    if (onMouseLeave != null) {
      setMouseLeaveAction(getStarterPositionSourceId(idRef.current), onMouseLeave);
    }
    if (onClick != null) {
      setOnClickAction(getStarterPositionSourceId(idRef.current), onClick);
    }
  }, [onMouseEnter, onMouseLeave, onClick]);

  return (
    <Source
      type="geojson"
      data={featureCollection}
      id={getStarterPositionSourceId(idRef.current)}
      key={getStarterPositionSourceId(idRef.current)}
      promoteId={'_id'}
    >
      <Layer
        id={`starter-position-filtered-dot-layer-${idRef.current}`}
        key={`starter-position-filtered-dot-layer-${idRef.current}`}
        type="circle"
        filter={['==', ['get', 'visualization', ['properties']], 'filtered-dot']}
        paint={filteredDotLayerPaint}
        beforeId={`starter-position-dot-layer-${idRef.current}`}
      />
      <Layer
        id={`starter-position-dot-layer-${idRef.current}`}
        key={`starter-position-dot-layer-${idRef.current}`}
        type="circle"
        filter={['==', ['get', 'visualization', ['properties']], 'dot']}
        layout={dotLayerLayout}
        paint={dotLayerPaint}
        beforeId={`starter-position-label-layer-${idRef.current}`}
      />
      <Layer
        id={`starter-position-label-layer-${idRef.current}`}
        key={`starter-position-label-layer-${idRef.current}`}
        type="symbol"
        layout={getlabelLayerLayout('starter-label-')}
        filter={['==', ['get', 'visualization', ['properties']], 'label']}
        paint={labelLayerPaint}
        beforeId={beforeId != null && layers.includes(beforeId) ? beforeId : undefined}
      />
    </Source>
  );
};

const labelLayerPaint: SymbolLayerSpecification['paint'] = {
  'text-color': ['get', 'textColor', ['properties']],
  'icon-opacity': 1,
  'text-opacity': 1,
};

const zoomScaled = (base: number | ExpressionSpecification): ExpressionSpecification => {
  const at = (factor: number): number | ExpressionSpecification =>
    typeof base === 'number' ? base * factor : ['*', base, factor];

  return ['interpolate', ['linear'], ['zoom'], 10, at(1), 14, at(1.5), 17, at(2.5), 20, at(4)];
};

const dotLayerLayout: CircleLayerSpecification['layout'] = {
  'circle-sort-key': ['get', 'sortKey', ['properties']],
};

const dotLayerPaint: CircleLayerSpecification['paint'] = {
  'circle-color': ['get', 'color', ['properties']],
  'circle-radius': zoomScaled(4),
  'circle-stroke-width': zoomScaled(['get', 'outlineWidthFactor', ['properties']]),
  'circle-stroke-color': ['get', 'outlineColor', ['properties']],
  'circle-translate-anchor': 'map',
};

const filteredDotLayerPaint: CircleLayerSpecification['paint'] = {
  ...dotLayerPaint,
  'circle-color': RacemapColors.LightLightGray,
  'circle-opacity': 0.4,
  'circle-stroke-color': RacemapColors.Gray,
  'circle-stroke-opacity': 0.9,
};

const labelTextField: ExpressionSpecification = [
  'format',
  ['get', 'startNumber', ['properties']],
  {},
  ['image', ['get', 'moveIcon', ['properties']]],
  ['image', ['get', 'offTrackIcon', ['properties']]],
  ['image', ['get', 'alertIcon', ['properties']]],
  ['get', 'alertCount', ['properties']],
  { 'font-scale': 0.85 },
  ['image', ['get', 'notificationIcon', ['properties']]],
  ['get', 'notificationCount', ['properties']],
  { 'font-scale': 0.85 },
];

const getlabelLayerLayout = (imagePrefix: string): SymbolLayerSpecification['layout'] => ({
  'icon-image': [
    'concat',
    imagePrefix,
    ['get', 'color', ['properties']],
    '-',
    ['get', 'outlineColor', ['properties']],
  ],
  'text-allow-overlap': true,
  'icon-allow-overlap': true,
  'icon-text-fit': 'both',
  'icon-text-fit-padding': [-1, 2, -1, 0],
  'text-field': labelTextField,
  'text-ignore-placement': false,
  'icon-ignore-placement': false,
  'text-anchor': 'left',
  'text-offset': [0.88, 0],
  'symbol-sort-key': ['get', 'sortKey', ['properties']],
  'text-size': 12,
  'icon-size': 1,
});

const useStatusIconImages = (map: MapRef | null): void => {
  useEffect(() => {
    if (map == null) return;
    const pixelRatio = window.devicePixelRatio || 1;

    Promise.all(
      (Object.keys(STARTER_STATUS_ICONS) as Array<StarterStatusIconKey>).flatMap((key) =>
        STATUS_ICON_COLORS.filter((color) => !map.hasImage(getStatusIconImageId(key, color))).map(
          async (color) => {
            const image = await getStatusIconImage(key, color);
            const imageId = getStatusIconImageId(key, color);
            if (!map.hasImage(imageId)) map.addImage(imageId, image, { pixelRatio });
          },
        ),
      ),
    );
  }, [map]);
};

const STARTER_LABEL_OUTLINE_COLORS = ['black', RacemapColors.AltRed, RacemapColors.LightOrange];

const useStarterMapLabels = (map: MapRef | null, colors: Set<string>): void => {
  const upsertMapLabels = useCallback(async () => {
    if (map == null) return;

    for (const color of colors) {
      for (const outlineColor of STARTER_LABEL_OUTLINE_COLORS) {
        const imageId = `starter-label-${color}-${outlineColor}`;
        if (!map.hasImage(imageId)) {
          await addImageToMap(map, imageId, {
            bgColor: color,
            borderColor: outlineColor,
            lineWidth: outlineColor === 'black' ? 1 : 2,
          });
        }
      }
    }
  }, [map, colors]);

  useEffect(() => {
    upsertMapLabels();
  }, [map, colors, upsertMapLabels]);
};

const addImageToMap = async (
  map: MapRef | null,
  imageId: string,
  options: GetTextLabelImageParams,
): Promise<void> => {
  if (map == null) return;
  const { image, dimensions } = await getTextLabelImage(options);
  const dpr = window.devicePixelRatio || 1;
  map.addImage(imageId, image, {
    stretchX: [[dimensions.textStartX * dpr, dimensions.textEndX * dpr]],
    stretchY: undefined,
    content: [
      dimensions.textStartX * dpr,
      dimensions.textStartY * dpr,
      dimensions.textEndX * dpr,
      dimensions.textEndY * dpr,
    ],
    pixelRatio: dpr,
  });

  return;
};
